/* Linova Bio — theme.js (no dependencies) */
(() => {
  'use strict';
  const T = window.LinovaTheme || {};
  const isRTL = document.documentElement.dir === 'rtl';
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = window.matchMedia('(pointer: fine)').matches;
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));

  /* ---------- Money ---------- */
  function money(cents) {
    const fmt = T.moneyFormat || '{{amount}} DH';
    const val = (cents / 100);
    const fix = (n, d, ts, ds) => {
      const p = n.toFixed(d).split('.');
      p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, ts);
      return p.join(ds);
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => {
      switch (k) {
        case 'amount_no_decimals': return fix(val, 0, ',', '.');
        case 'amount_with_comma_separator': return fix(val, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fix(val, 0, '.', ',');
        case 'amount_with_space_separator': return fix(val, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return fix(val, 0, ' ', ',');
        default: return fix(val, 2, ',', '.');
      }
    }).replace(/<[^>]*>/g, '');
  }

  /* ---------- Header: scrolled state + drawer ---------- */
  const header = $('[data-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll(); window.addEventListener('scroll', onScroll, { passive: true });
    const drawer = $('[data-drawer]', header);
    const openBtn = $('[data-drawer-open]', header);
    const setDrawer = (open) => {
      if (!drawer) return;
      drawer.hidden = !open;
      openBtn && openBtn.setAttribute('aria-expanded', String(open));
      document.body.style.overflow = open ? 'hidden' : '';
    };
    openBtn && openBtn.addEventListener('click', () => setDrawer(true));
    $$('[data-drawer-close]', header).forEach(b => b.addEventListener('click', () => setDrawer(false)));
    document.addEventListener('keydown', e => { if (e.key === 'Escape') setDrawer(false); });
  }

  /* ---------- Reveal on scroll ---------- */
  const reveals = $$('.reveal');
  if ('IntersectionObserver' in window && !reduce) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(en => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    reveals.forEach((el, i) => { el.style.transitionDelay = (i % 4) * 70 + 'ms'; io.observe(el); });
  } else {
    reveals.forEach(el => el.classList.add('is-in'));
  }

  /* ---------- 3D tilt cards ---------- */
  if (fine && !reduce) {
    $$('[data-tilt]').forEach(el => {
      const max = el.hasAttribute('data-tilt-soft') ? 5 : 10;
      let raf = 0;
      el.addEventListener('pointermove', e => {
        const r = el.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width - 0.5;
        const y = (e.clientY - r.top) / r.height - 0.5;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          el.classList.add('is-tilting');
          el.style.transform = `perspective(900px) rotateX(${(-y * max).toFixed(2)}deg) rotateY(${(x * max).toFixed(2)}deg) translateZ(0)`;
        });
      });
      el.addEventListener('pointerleave', () => {
        cancelAnimationFrame(raf);
        el.classList.remove('is-tilting');
        el.style.transform = '';
      });
    });
  }

  /* ---------- Parallax (mouse depth) ---------- */
  if (fine && !reduce) {
    const stages = $$('[data-hero3d], [data-parallax-stage]');
    stages.forEach(stage => {
      const layers = $$('[data-depth]', stage);
      if (!layers.length) return;
      stage.addEventListener('pointermove', e => {
        const r = stage.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width - 0.5;
        const y = (e.clientY - r.top) / r.height - 0.5;
        layers.forEach(l => {
          const d = parseFloat(l.dataset.depth) || 0.5;
          l.style.transform = `translate3d(${(x * d * 30).toFixed(1)}px, ${(y * d * 30).toFixed(1)}px, 0)`;
        });
      });
      stage.addEventListener('pointerleave', () => layers.forEach(l => { l.style.transform = ''; }));
    });
  }

  /* ---------- Hero 3D ring (auto-rotate + drag) ---------- */
  $$('[data-ring-stage]').forEach(stage => {
    const ring = $('[data-ring]', stage);
    if (!ring) return;
    const items = $$('.ring__item', ring);
    const n = items.length || 1;
    const step = 360 / n;
    let rot = 0, vel = reduce ? 0 : (isRTL ? 0.12 : -0.12);
    const base = vel;
    let dragging = false, lastX = 0, lastT = 0, hover = false, visible = true;

    const apply = () => {
      ring.style.setProperty('--ring-rot', rot + 'deg');
      // mark the item currently facing the viewer
      const norm = ((-rot % 360) + 360) % 360;
      const front = Math.round(norm / step) % n;
      items.forEach((it, i) => {
        it.classList.toggle('is-front', i === front);
        const z = Math.cos(((i * step) + rot) * Math.PI / 180);
        it.style.setProperty('--depth', z.toFixed(3));
      });
    };
    const tick = () => {
      if (visible && !dragging) {
        if (!hover) vel += (base - vel) * 0.02; else vel *= 0.92;
        rot += vel;
        apply();
      }
      requestAnimationFrame(tick);
    };
    apply();
    requestAnimationFrame(tick);

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([en]) => { visible = en.isIntersecting; }).observe(stage);
    }
    if (fine) {
      stage.addEventListener('pointerenter', () => { hover = true; });
      stage.addEventListener('pointerleave', () => { hover = false; });
    }
    stage.addEventListener('pointerdown', e => {
      dragging = true; lastX = e.clientX; lastT = performance.now();
      stage.classList.add('is-dragging');
      stage.setPointerCapture && stage.setPointerCapture(e.pointerId);
    });
    stage.addEventListener('pointermove', e => {
      if (!dragging) return;
      const now = performance.now();
      const dx = e.clientX - lastX;
      rot += dx * 0.35;
      vel = (dx * 0.35) / Math.max(16, now - lastT) * 16;
      lastX = e.clientX; lastT = now;
      apply();
    });
    const end = () => { dragging = false; stage.classList.remove('is-dragging'); };
    stage.addEventListener('pointerup', end);
    stage.addEventListener('pointercancel', end);
    // Prevent link click after a drag
    let moved = 0;
    stage.addEventListener('pointerdown', e => { moved = e.clientX; });
    stage.addEventListener('click', e => { if (Math.abs(e.clientX - moved) > 8) e.preventDefault(); }, true);
  });

  /* ---------- Sticky CTA (mobile) ---------- */
  const sticky = $('[data-sticky-cta]');
  const order = $('#order');
  if (sticky) {
    sticky.hidden = false;
    const link = $('a', sticky);
    if (!order && link) link.setAttribute('href', (T.rootUrl || '/').replace(/\/?$/, '/') + 'collections/all');
    let orderVisible = false;
    if (order && 'IntersectionObserver' in window) {
      new IntersectionObserver(([en]) => { orderVisible = en.isIntersecting; update(); }, { threshold: 0.05 }).observe(order);
    }
    function update() { sticky.classList.toggle('is-visible', window.scrollY > 500 && !orderVisible); }
    window.addEventListener('scroll', update, { passive: true }); update();
  }
  $$('[data-scroll-order]').forEach(a => a.addEventListener('click', e => {
    const target = $('#order');
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    const first = $('input[name="name"]', target);
    first && setTimeout(() => first.focus({ preventScroll: true }), 600);
  }));

  /* ---------- Product gallery thumbs ---------- */
  $$('[data-pdp]').forEach(pdp => {
    const main = $('[data-gallery-main]', pdp);
    $$('[data-thumb]', pdp).forEach(btn => btn.addEventListener('click', () => {
      const img = main && $('img', main);
      if (!img) return;
      img.src = btn.dataset.src; img.srcset = btn.dataset.srcset;
      $$('[data-thumb]', pdp).forEach(b => b.classList.toggle('is-active', b === btn));
    }));
  });

  /* ---------- COD order form (native, no app) ---------- */
  $$('[data-order-form]').forEach(form => {
    const S = T.strings || {};
    const totalEl = $('[data-total]', form);
    const nowEl = $('[data-price-now]');
    const wasEl = $('[data-price-was]');
    const errEl = $('[data-order-error]', form);
    const submit = $('[data-order-submit]', form);
    const label = submit && $('[data-submit-label]', submit);
    const upsell = $('[data-upsell] input', form);
    const qtyInput = $('[data-qty-input]', form);

    const selectedVariant = () => form.querySelector('input[name="variant"]:checked') || form.querySelector('input[name="variant"][type="hidden"]');
    const qty = () => Math.max(1, Math.min(10, parseInt(qtyInput ? qtyInput.value : 1, 10) || 1));

    function recalc() {
      const v = selectedVariant();
      if (!v) return;
      const price = parseInt(v.dataset.price, 10) || 0;
      const compare = parseInt(v.dataset.compare, 10) || 0;
      let total = price * qty();
      if (upsell && upsell.checked) total += parseInt(upsell.dataset.price, 10) || 0;
      if (totalEl) totalEl.textContent = money(total);
      if (nowEl) nowEl.textContent = money(price);
      if (wasEl) { wasEl.hidden = !(compare > price); if (compare > price) wasEl.textContent = money(compare); }
    }

    $$('[data-offer]', form).forEach(lbl => {
      const input = $('input', lbl);
      input.addEventListener('change', () => {
        $$('[data-offer]', form).forEach(l => l.classList.toggle('is-selected', $('input', l).checked));
        recalc();
      });
    });
    if (upsell) upsell.addEventListener('change', () => {
      upsell.closest('[data-upsell]').classList.toggle('is-checked', upsell.checked);
      recalc();
    });
    if (qtyInput) {
      $('[data-qty-minus]', form).addEventListener('click', () => { qtyInput.value = Math.max(1, qty() - 1); recalc(); });
      $('[data-qty-plus]', form).addEventListener('click', () => { qtyInput.value = Math.min(10, qty() + 1); recalc(); });
      qtyInput.addEventListener('input', recalc);
    }
    recalc();

    const showErr = (msg) => { if (!errEl) return; errEl.textContent = msg; errEl.hidden = !msg; };
    const normPhone = (p) => {
      let d = String(p || '').replace(/[^\d+]/g, '');
      if (d.startsWith('00')) d = '+' + d.slice(2);
      if (d.startsWith('+212')) d = '0' + d.slice(4);
      else if (d.startsWith('212') && d.length === 12) d = '0' + d.slice(3);
      return d;
    };
    const validPhone = (p) => /^0[5-7]\d{8}$/.test(p);

    form.addEventListener('input', e => { if (e.target.classList) e.target.classList.remove('is-invalid'); showErr(''); });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      showErr('');
      const fields = $$('.field__input[required]', form);
      let ok = true;
      fields.forEach(f => { const bad = !f.value.trim(); f.classList.toggle('is-invalid', bad); if (bad) ok = false; });
      if (!ok) { showErr(S.required || 'Required'); fields.find(f => f.classList.contains('is-invalid')).focus(); return; }

      const phoneEl = form.elements.phone;
      const phone = normPhone(phoneEl.value);
      if (!validPhone(phone)) { phoneEl.classList.add('is-invalid'); showErr(S.phone || 'Invalid phone'); phoneEl.focus(); return; }

      const v = selectedVariant();
      if (!v) return;
      const name = form.elements.name.value.trim();
      const city = form.elements.city.value.trim();
      const address = form.elements.address ? form.elements.address.value.trim() : '';
      const parts = name.split(/\s+/);
      const first = parts.shift() || name;
      const last = parts.join(' ') || first;

      submit.classList.add('is-loading');
      if (label) label.textContent = S.processing || '…';

      // Build a Shopify cart permalink: replaces the cart and opens checkout pre-filled.
      const lines = [`${v.value}:${qty()}`];
      if (upsell && upsell.checked) lines.push(`${upsell.value}:1`);
      const q = new URLSearchParams();
      q.set('checkout[shipping_address][first_name]', first);
      q.set('checkout[shipping_address][last_name]', last);
      q.set('checkout[shipping_address][address1]', address || city);
      q.set('checkout[shipping_address][city]', city);
      q.set('checkout[shipping_address][country]', 'MA');
      q.set('checkout[shipping_address][phone]', phone);
      q.set('attributes[Téléphone]', phone);
      q.set('attributes[Ville]', city);
      q.set('attributes[Source]', 'COD form');
      const code = form.dataset.upsellCode;
      if (upsell && upsell.checked && code) q.set('discount', code);

      try {
        // Track (Meta / TikTok pixels if installed via Shopify customer events or theme)
        if (window.fbq) window.fbq('track', 'InitiateCheckout');
        if (window.ttq) window.ttq.track('InitiateCheckout');
      } catch (_) {}

      const root = (T.rootUrl || '/').replace(/\/$/, '');
      window.location.href = `${root}/cart/${lines.join(',')}?${q.toString()}`;
    });

    // Reset button if user navigates back (bfcache)
    window.addEventListener('pageshow', () => {
      submit && submit.classList.remove('is-loading');
      if (label && label.dataset.orig) label.textContent = label.dataset.orig;
    });
    if (label) label.dataset.orig = label.textContent;
  });
})();


/* ---------- EasySell COD button: habillage Linova ---------- */
(function () {
  var root = document.documentElement;
  function v(name, fb) {
    var val = getComputedStyle(root).getPropertyValue(name).trim();
    return val || fb;
  }
  function skin() {
    var accent = v('--c-accent', '#F0592A');
    var btns = document.querySelectorAll('.es-popup-button, .es-cta-btn, button.es-cta');
    btns.forEach(function (el) {
      if (el.dataset.lnvSkin) return;
      el.dataset.lnvSkin = '1';
      var s = el.style;
      s.setProperty('background', 'linear-gradient(135deg, color-mix(in srgb, ' + accent + ' 85%, #FFB38A), ' + accent + ' 60%, color-mix(in srgb, ' + accent + ' 80%, #8A1E00))', 'important');
      s.setProperty('color', '#fff', 'important');
      s.setProperty('border', '0', 'important');
      s.setProperty('border-radius', '999px', 'important');
      s.setProperty('padding', '18px 24px', 'important');
      s.setProperty('font-weight', '800', 'important');
      s.setProperty('font-size', '1.05rem', 'important');
      s.setProperty('font-family', v('--f-body', 'inherit'), 'important');
      s.setProperty('box-shadow', '0 14px 28px -14px ' + accent, 'important');
      s.setProperty('letter-spacing', '.2px', 'important');
    });
  }
  skin();
  document.addEventListener('DOMContentLoaded', skin);
  var tries = 0;
  var iv = setInterval(function () { skin(); if (++tries > 20) clearInterval(iv); }, 600);
  if (window.MutationObserver) {
    new MutationObserver(skin).observe(document.documentElement, { childList: true, subtree: true });
  }
})();


/* ---------- Hero slider ---------- */
(function () {
  var roots = document.querySelectorAll('[data-hero-slider]');
  Array.prototype.forEach.call(roots, function (root) {
    var slides = [].slice.call(root.querySelectorAll('[data-hslide]'));
    if (slides.length < 2) return;
    var dots = [].slice.call(root.querySelectorAll('[data-hslider-dot]'));
    var delay = (parseFloat(root.getAttribute('data-autoplay')) || 0) * 1000;
    var i = 0, timer = null;

    function go(n) {
      i = (n + slides.length) % slides.length;
      slides.forEach(function (s, k) { s.classList.toggle('is-active', k === i); });
      dots.forEach(function (d, k) {
        d.classList.toggle('is-active', k === i);
        d.setAttribute('aria-selected', k === i ? 'true' : 'false');
      });
    }
    function next() { go(i + 1); }
    function prev() { go(i - 1); }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }
    function start() { stop(); if (delay > 0) timer = setInterval(next, delay); }

    var btnNext = root.querySelector('[data-hslider-next]');
    var btnPrev = root.querySelector('[data-hslider-prev]');
    if (btnNext) btnNext.addEventListener('click', function () { next(); start(); });
    if (btnPrev) btnPrev.addEventListener('click', function () { prev(); start(); });
    dots.forEach(function (d) {
      d.addEventListener('click', function () { go(parseInt(d.getAttribute('data-index'), 10) || 0); start(); });
    });

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);

    var x0 = null;
    root.addEventListener('touchstart', function (e) { x0 = e.touches[0].clientX; stop(); }, { passive: true });
    root.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var dx = e.changedTouches[0].clientX - x0;
      if (Math.abs(dx) > 40) { if (dx < 0) next(); else prev(); }
      x0 = null; start();
    }, { passive: true });

    document.addEventListener('visibilitychange', function () { if (document.hidden) stop(); else start(); });

    go(0); start();
  });
})();
